import { Alert, AlertVariant } from "@next-platform/client-common-spicy-ui";
import { useTranslation, useUserSettings } from "@next-platform/client-common-core";
import { useCallback, useMemo, useState } from "react";

import ConfirmationModal from "@/components/common/ConfirmationModal";
import { CHAT_PAGE_WIDTH } from "@/constants";

import { AlertBubbleProps, AlertBubbleVariant } from "./types";

const AlertBubble = ({ error, invalidUrlError, character }: AlertBubbleProps) => {
  const { t } = useTranslation();
const [showUnblockModal, setShowUnblockModal] = useState(false);
  const { onUserUnblockCreator, isLoading: isBlockLoading } = useUserSettings();

  const toggleUnblockModal = useCallback((visible: boolean) => {
    setShowUnblockModal(visible);
  }, []);

  const handleUnblockCreator = useCallback(async () => {
    if (character?.creator_user_id) {
      await onUserUnblockCreator(character.creator_user_id);
    }
  }, [character?.creator_user_id]);

  const alertConfig: Record<
    string,
    {
      message: string;
      actionTitle?: string;
      actionHandler?: () => void;
    }
  > = {
    [AlertBubbleVariant.INVALID_ACCESS]: {
      message: t("chat:page.alert.invalidAccess.description"),
    },
    [AlertBubbleVariant.BLOCKED_BOT_CREATOR]: {
      message: t("chat:page.alert.blockedCreator.description"),
      actionTitle: t("chat:page.alert.blockedCreator.cta.unblock"),
      actionHandler: () => toggleUnblockModal(true),
    },
  };

  const config = alertConfig[error] || {
    message: t("error:fallbackError"),
  };

  const message = useMemo(() => invalidUrlError?.message ?? config.message, [invalidUrlError, config.message]);

  if (!message) return null;

  return (
    <>
      <div className="w-full mx-auto px-md" style={{ maxWidth: CHAT_PAGE_WIDTH }}>
        <Alert
          visible={true}
          title={message}
          actionHandler={config.actionHandler}
          actionTitle={config.actionTitle ?? ""}
          variant={AlertVariant.ERROR}
          isDismissable={false}
        />
      </div>
      <ConfirmationModal
        heading={t("creator:modal.unblock.title")}
        isVisible={showUnblockModal}
        confirmationMessage={t("creator:modal.unblock.description", { username: character?.creator_username })}
        handleClose={() => toggleUnblockModal(false)}
        onUserConfirmation={handleUnblockCreator}
        isLoading={isBlockLoading}
        actionButtonText={t("common:yes")}
        discardButtonText={t("common:no")}
      />
    </>
  );
};

export default AlertBubble;
